import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { Accordion } from '@sorare/core/src/atoms/layout/Accordion/index.tsx';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { SeriesBoardTimer } from '@sorare/core/src/components/commonGame/SeriesBoardTimer/index.tsx';
import {
  ANY_SPORT_SERIES_PLAY,
  ANY_SPORT_SERIES_SQUAD,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary.ts';
import { useCommonGameSet } from '@sorare/core/src/set';

import squadImg from '#src/assets/squad.webp';
import { useIsSquadEnabled } from '#src/hooks/squad/useIsSquadEnabled.ts';

import type { SeriesBoardTrckSwitcher_squad } from './__generated__/index.graphql.ts';

const Root = styled.div`
  height: calc(8 * var(--unit));
  overflow: visible;
  &.with-padding {
    padding: var(--intermediate-unit) 0;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1;
  background: rgba(var(--c-rgb-black), 0.8);
`;

const AccordionWrapper = styled.div`
  position: relative;
  z-index: 2;
`;

type Props = {
  squad: Nullable<SeriesBoardTrckSwitcher_squad>;
  selected: 'individual' | 'squad';
  isReplay?: boolean;
};

export const SeriesBoardTrckSwitcher = ({
  squad,
  selected,
  isReplay,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const commonGameSet = useCommonGameSet();
  const { SetIcon } = commonGameSet.visual.brand;
  const isSquadEnabled = useIsSquadEnabled() && !isReplay;
  const [expanded, setExpanded] = useState(false);

  const individualTrack = (
    <Horizontal>
      <SetIcon size={5} />
      <Vertical gap={0.5}>
        <BrandS>
          {isSquadEnabled ? (
            <Link to={generateSportContextPath(ANY_SPORT_SERIES_PLAY)}>
              {commonGameSet.displayName}
            </Link>
          ) : (
            commonGameSet.displayName
          )}
        </BrandS>
        {!!commonGameSet.endDate && (
          <LinkOther as="div">
            <SeriesBoardTimer endDate={commonGameSet.endDate} />
          </LinkOther>
        )}
      </Vertical>
    </Horizontal>
  );
  const squadTrack = (
    <Link to={generateSportContextPath(ANY_SPORT_SERIES_SQUAD)}>
      <Horizontal>
        <img src={squadImg} width={40} alt="Squad Track" />
        <Vertical>
          <BrandS>
            {squad ? squad.name : <FormattedMessage {...glossary.squads} />}
          </BrandS>
        </Vertical>
      </Horizontal>
    </Link>
  );

  if (!isSquadEnabled) {
    return <Root className="with-padding">{individualTrack}</Root>;
  }

  return (
    <Root>
      {expanded ? <Backdrop /> : null}
      <AccordionWrapper>
        <Accordion
          noBorder
          noHorizontalPadding
          title={selected === 'individual' ? individualTrack : squadTrack}
          onExpandChange={setExpanded}
        >
          {selected === 'individual' ? squadTrack : individualTrack}
        </Accordion>
      </AccordionWrapper>
    </Root>
  );
};

SeriesBoardTrckSwitcher.fragments = {
  squad: gql`
    fragment SeriesBoardTrckSwitcher_squad on Squad {
      id
      name
    }
  ` as TypedDocumentNode<SeriesBoardTrckSwitcher_squad>,
};
